'use client';

import { usePathname, useRouter } from 'next/navigation';
import { FC, useEffect } from 'react';
import useSWR from 'swr';
import { shallow } from 'zustand/shallow';

import { addWatchlistItem, getWatchlistItems } from 'src/data/WatchListApi/WatchlistApi';
import { WatchlistItemResponse } from 'src/data/WatchListApi/WatchlistApi.types';
import { ToasterService } from 'src/general/components/Toaster/ToasterService';
import { authStore } from 'src/stores/authStore';
import { watchlistStore } from 'src/stores/watchListStore';

export const WatchListManager: FC = () => {
  const router = useRouter();
  const pathname = usePathname();

  const { isUserLoggedIn, userAuthDataLoading } = authStore(
    (state) => ({
      isUserLoggedIn: state.isUserLoggedIn,
      userAuthDataLoading: state.userAuthDataLoading,
    }),
    shallow,
  );
  const { emptyWatchlist, setWatchlistData } = watchlistStore(
    (state) => ({
      emptyWatchlist: state.emptyWatchlist,
      setWatchlistData: state.setWatchlistData,
    }),
    shallow,
  );

  const loadWatchlistData: () => Promise<void | WatchlistItemResponse[]> = async () => {
    const result = await getWatchlistItems();
    setWatchlistData(result ?? null);
    return result;
  };

  const addToWatchList: (sku: string) => Promise<void> = async (sku: string) => {
    const result = await addWatchlistItem({ sku });
    setWatchlistData(result ?? null);
  };

  useSWR(isUserLoggedIn && !userAuthDataLoading ? 'watchlist' : null, loadWatchlistData, {
    revalidateOnFocus: true,
    focusThrottleInterval: 30000,
  });

  const handleWatchlist = async () => {
    const currentUrl = new URL(window.location.toString());
    const watchlistSku = currentUrl.searchParams.get('watchlistSku');
    if (isUserLoggedIn && !userAuthDataLoading) {
      if (watchlistSku) {
        await addToWatchList(watchlistSku);
        ToasterService.success({ message: 'Vehicle has been added to your watchlist.' });
        currentUrl.searchParams.delete('watchlistSku');
        router.replace(currentUrl.toString());
      }
    } else {
      // empty watchlist store when logged-out
      emptyWatchlist();
    }
  };

  useEffect(() => {
    handleWatchlist();
  }, [isUserLoggedIn, userAuthDataLoading, pathname]);

  return null;
};
